export type ServiceImage = {
  src: string;
  alt: string;
  caption?: string;
};

export type ServiceContentSection = {
  heading: string;
  paragraphs: string[];
};

export type ServiceFaq = {
  question: string;
  answer: string;
};

export type ServiceItem = {
  slug: string;
  title: string;
  shortDesc: string;
  desc: string;
  features: string[];
  images: ServiceImage[];
  contentSections?: ServiceContentSection[];
  keyTasks?: string[];
  whyTrustUs?: string[];
  faqs?: ServiceFaq[];
};

export const serviceItems: ServiceItem[] = [
  {
    slug: "private-drainage",
    title: "Private Drainage",
    shortDesc: "Residential drainage systems, repairs, and stormwater solutions for private properties.",
    desc: "Residential drainage solutions for homes, renovations, and private developments. We install, repair, and upgrade stormwater and wastewater systems with a clean finish and code-compliant workmanship.",
    features: [
      "Residential stormwater systems",
      "Wastewater connections",
      "Drainage repairs and upgrades",
      "French and channel drains",
      "Soak holes and retention",
      "Property drainage assessments"
    ],
    images: [
      { src: "/media/private-drainage/private-drainage-01.jpeg", alt: "Residential stormwater pipe installation in Papakura, Auckland", caption: "Papakura • Stormwater Pipe Trenching & Alignment" },
      { src: "/media/private-drainage/private-drainage-02.jpeg", alt: "Excavation and GAP20 aggregate pipe bedding in Hillsborough, Auckland", caption: "Hillsborough • Laser-Graded GAP20 Pipe Bedding" },
      { src: "/media/private-drainage/private-drainage-03.jpeg", alt: "Completed private drain connection to boundary in Manukau", caption: "Manukau • Property Boundary Connection & Inspection Eye" },
      { src: "/media/private-drainage/private-drainage-04.jpeg", alt: "Subsoil drainage trench with Novacoil pipe in North Shore", caption: "North Shore • Geotextile-Wrapped Novacoil Subsoil Drain" },
      { src: "/media/private-drainage/private-drainage-05.jpeg", alt: "Backfilled drainage trench ready for reinstatement in Franklin", caption: "Franklin • Layered Trench Backfill & Compaction" }
    ],
    contentSections: [
      {
        heading: "Private Drain Installation for Auckland Homes and Renovations",
        paragraphs: [
          "Properly installed private drainage protects your home foundation, prevents surface flooding, and ensures wastewater and stormwater flow cleanly to your property boundary. Craft Civil Contractors installs and upgrades on-site drainage systems across Auckland, integrating household pipework with [stormwater detention tanks](/services/rainwater-tank-install) and boundary connection points.",
          "Before digging, our team checks site levels, soil conditions, and connection boundaries using Auckland Council GeoMaps GIS data. We handle the full installation sequence—trench excavation, GAP20 aggregate bedding, laying Class SN4 or SN8 PVC pipes with correct falls, jointing, backfilling, and compaction.",
          "Every installation is planned around your site's specific ground conditions, ensuring durable performance under heavy Auckland rainfall. We complete low-pressure testing, verify compliance with NZ Building Code E1 and G13 standards, and provide scaled As-Built plans for council records.",
          "Whether you are building a new home, adding an extension, or replacing an old failing line, we provide transparent quotes and practical guidance as part of our [comprehensive Auckland drainage services](/services/drainage)."
        ]
      },
      {
        heading: "Subsoil Drainage, Soakholes & Stormwater Pipe Systems",
        paragraphs: [
          "Managing ground water requires more than basic surface channels. We design and install complete private drainage systems tailored to your site—from perforated Novacoil subsoil lines and French drains for soggy lawns to driveway channel grates, specialized [Onehunga volcanic basalt soakhole drilling](/service-areas/central-isthmus/onehunga), and inspection openings. If you are dealing with an urgent sewer overflow, we also provide [emergency drain clearing](/services/drain-unblocking).",
          "All stormwater and sewer laterals are laid with laser-verified falls to ensure self-cleansing velocity and prevent sediment buildup. We ensure full compliance with NZ Building Code E1 (Surface Water) and G13 (Foul Water) standards on every job.",
          "As a [certified drainlaying contractor](/services/drainlaying), we prioritize permanent, compliant drainage construction over temporary fixes, ensuring your property remains dry and structurally sound for decades."
        ]
      },
      {
        heading: "Retaining Wall Subsoil Relief, French Drains & Novacoil Geotextile Bedding",
        paragraphs: [
          "Retaining walls and sloped Auckland sections require dedicated subsoil drainage to eliminate hydrostatic water pressure behind concrete or timber walls. We install heavy-duty perforated Novacoil pipes wrapped in non-woven geotextile filter fabric, bedded and surrounded by free-draining GAP20 or scoria aggregate. This prevents fine silt and clay from blinding the pipe perforations, ensuring continuous sub-surface water diversion away from building footings, particularly across [West Auckland heavy clay catchments](/service-areas/west-auckland/new-lynn-henderson).",
          "For flat or flood-prone lawns, French drains and surface channel grates capture excess overland flow during intense weather events, channelling runoff directly into council-approved soakholes or boundary connection points."
        ]
      },
      {
        heading: "Earthenware Drain Repair vs Full PVC Replacement",
        paragraphs: [
          "Older Auckland homes frequently suffer from fractured earthenware clay pipes, displaced mortared joints, and persistent tree root intrusions. Where CCTV camera surveys reveal localized joint cracks, targeted point repairs can restore flow without excavating entire driveways.",
          "However, if lines show extensive structural collapse or backfall, replacing aging earthenware with heavy-wall Class SN4 or SN8 PVC-U provides a permanent, leak-proof solution with smooth internal bores that prevent recurring clogs and root re-entry."
        ]
      }
    ],
    keyTasks: [
      "Installation of private sewer lateral drains",
      "Installation of stormwater drainage pipework",
      "Connections for new private drainage networks",
      "Novacoil subsoil and French drain installation",
      "Driveway and patio channel drain installation",
      "GAP20 aggregate pipe bedding and backfill compaction",
      "Inspection opening (IO) and gully trap installation",
      "Existing drain connection and repair works",
      "Drainage line extensions and soakhole construction",
      "As-built drainage plan preparation for Council"
    ],
    whyTrustUs: [
      "We are licensed and insured for private drainage projects",
      "Our 15+ years of practical civil construction experience guides every job",
      "We verify point of connection boundaries using Auckland Council GeoMaps",
      "Our team checks pipe gradients and laser alignment closely (NZBC E1/G13)",
      "We use GAP20 aggregate bedding and proven installation techniques",
      "Our workmanship gives pipe joints and inspection openings proper attention",
      "We offer competitive rates without compromising workmanship",
      "Our communication stays clear throughout every project phase",
      "We finish reliably, providing As-Built drawings for site handover"
    ],
    faqs: [
      {
        question: "Where does private drainage end and public drainage begin in Auckland?",
        answer: "Private drainage encompasses all stormwater and sewer pipes on your property up to the boundary or point of connection. Public mains are managed by Watercare or Auckland Council. We verify connection boundaries using Council GeoMaps."
      },
      {
        question: "What factors influence the cost of private drain installation in Auckland?",
        answer: "Costs vary based on trench depth, pipe length, ground conditions (such as rock or clay), site access, and whether subsoil Novacoil or soakholes are required. We provide clear site-specific quotes after evaluating site levels."
      },
      {
        question: "What is an As-Built drainage plan and why is it needed?",
        answer: "An As-Built plan is a scaled drawing documenting exact pipe depths, layout, and connection points of your completed drainage system. It is submitted to Auckland Council to obtain your Code Compliance Certificate (CCC) and update GIS records."
      },
      {
        question: "How do French drains and Novacoil subsoil systems prevent surface flooding?",
        answer: "Perforated Novacoil wrapped in geotextile fabric and surrounded by GAP20 aggregate collects groundwater and diverts it away from house footings into stormwater mains, preventing soggy lawns and foundation water damage."
      },
      {
        question: "How does subsoil drainage prevent water buildup behind retaining walls?",
        answer: "Continuous perforated Novacoil lines bedded in scoria or GAP20 aggregate behind retaining walls relieve hydrostatic water pressure, preventing ground saturation that could cause timber or concrete walls to bow and fail."
      },
      {
        question: "Do I need a building consent to replace private drainage in Auckland?",
        answer: "Like-for-like repairs or simple line replacements often do not require consent, but new installations, major alterations, or connections to public mains require Building Consent with a PS3 Producer Statement."
      }
    ]
  },
  {
    slug: "public-drainage",
    title: "Public Drainage",
    shortDesc: "Drainage infrastructure for roads, subdivisions, and commercial or council projects.",
    desc: "Public drainage delivery for subdivisions, road corridors, and commercial sites. We handle large-scale stormwater and wastewater infrastructure with a focus on compliance, durability, and efficient delivery.",
    features: [
      "Subdivision drainage infrastructure",
      "Council-compliant installations",
      "Commercial stormwater systems",
      "Large diameter pipeline installation",
      "Road corridor drainage works",
      "Inspection chambers and manholes"
    ],
    images: [
      { src: "/media/public-drainage/public-drainage-01.jpeg", alt: "Public stormwater main pipeline installation in South Auckland", caption: "South Auckland • Heavy Public Stormwater Trunk Line" },
      { src: "/media/public-drainage/public-drainage-02.jpeg", alt: "Reinforced precast concrete manhole installation in Manukau", caption: "Manukau • 1050mm Precast Concrete Manhole Chamber" },
      { src: "/media/public-drainage/public-drainage-03.jpeg", alt: "Deep trench excavation with safety shoring in Papakura", caption: "Papakura • Deep Excavation with Safety Trench Box" },
      { src: "/media/public-drainage/public-drainage-04.jpeg", alt: "Public catch pit construction and kerb connection in West Auckland", caption: "West Auckland • Roadside Catch Pit & Silt Trap" },
      { src: "/media/public-drainage/public-drainage-05.jpeg", alt: "Completed public drainage main extension in Central Auckland", caption: "Central Auckland • Public Drainage Extension & Tie-In" }
    ],
    contentSections: [
      {
        heading: "Public Drainage Infrastructure for Subdivisions and Council Projects",
        paragraphs: [
          "Constructing public drainage infrastructure requires precise laser grading, heavy plant capability, and strict adherence to Auckland Council and Watercare engineering standards. [Craft Civil Contractors](/) constructs and extends public stormwater and wastewater networks, delivering dependable infrastructure alongside [commercial water main installations](/services/plumbing).",
          "Before excavation begins, our civil team locates existing underground utilities, verifies council GIS records, and establishes safe trench shoring. We manage large-diameter stormwater pipe laying, precast concrete manholes, street catchpits, culverts, and live network tie-ins, delivering [civil drainage infrastructure](/services/drainage) and [certified public drainlaying](/services/drainlaying) compliant with Auckland Council Code of Practice Chapters 4 and 5."
        ]
      },
      {
        heading: "Watercare Connections, Main Extensions & Subdivision Sign-Off",
        paragraphs: [
          "Connecting new developments to municipal infrastructure demands thorough planning and compliance documentation. We handle public sewer main extensions, manhole core drilling, and live asset tie-ins for residential subdivisions and commercial sites, coordinating smoothly with [reticulated wastewater system upgrades](/services/wastewater-systems).",
          "Our site management addresses dewatering, trench shoring shields, [concrete vehicle crossing reinstatements](/services/concrete-civil), and road corridor traffic safety. We assist developers with Watercare Works Over Asset Approvals, Certificate of Connection (CoC) sign-offs, and As-Built drawings required for Section 224(c) subdivision title clearances across Auckland."
        ]
      },
      {
        heading: "Watercare Works Over Asset Approvals & Network Protection",
        paragraphs: [
          "Excavating or constructing within close proximity (1.5m to 2.0m) of public Watercare water, stormwater, or sewer mains requires formal Works Over Asset Approval to protect municipal assets from settlement or structural damage. Craft Civil Contractors manages the end-to-end engineering approval process—from digital GIS utility mapping and pre-construction CCTV surveys to engineered bridging piles and certified trench shoring shields.",
          "Our team coordinates directly with Watercare asset engineers, conducting site inspections, vibration-controlled digging, and hydro-vac non-destructive potholing around live underground utility corridors."
        ]
      },
      {
        heading: "Section 224(c) RMA Subdivision Sign-Off & Public Infrastructure Compliance",
        paragraphs: [
          "For residential and commercial subdividers, obtaining Section 224(c) title clearance under the Resource Management Act requires all public stormwater and sewer main extensions to be constructed strictly to Auckland Council Code of Practice (Chapters 4 and 5) engineering standards.",
          "We install precast concrete manholes, street catchpits, culverts, and live network tie-ins—including certified municipal and Veolia connections across [South Auckland subdivisions in Papakura and Drury](/service-areas/south-auckland/papakura-drury)—submitting certified As-Built drawings and hydrostatic test certificates to expedite title issuance."
        ]
      }
    ],
    keyTasks: [
      "Public stormwater main pipeline installation",
      "Public sewer drainage main construction",
      "Pre-cast concrete manhole installation and coring",
      "Construction of public catch pits and inlets",
      "Culvert installation and wingwall construction",
      "Watercare public network connection and tie-in works",
      "Deep trench excavation, shoring, and dewatering",
      "Subdivision public drainage main extensions",
      "Road corridor trench backfilling and asphalt reinstatement",
      "As-built submission for Section 224(c) subdivision sign-off"
    ],
    whyTrustUs: [
      "We bring 15+ years of hands-on experience to public civil infrastructure works",
      "Our licensed and insured team supports dependable, compliant delivery",
      "We coordinate Watercare Works Over approvals and Certificate of Connection (CoC) sign-offs",
      "Our precision level checks maintain accurate drainage gradients",
      "We use modern heavy excavators, shoring boxes, and compaction equipment",
      "Our planning addresses utilities, site safety, and road corridor reinstatement",
      "We deliver durable workmanship supporting Section 224c subdivision certification"
    ],
    faqs: [
      {
        question: "What is a Watercare Works Over Asset Approval and when is it required?",
        answer: "A Works Over approval is a formal permit issued by Watercare before excavating or constructing within close proximity (typically 1.5m–2m) of public sewer or water mains in Auckland to ensure public assets remain protected."
      },
      {
        question: "How does public drainage support Section 224(c) subdivision title clearance?",
        answer: "Section 224(c) requires all public civil infrastructure (stormwater/sewer main extensions, concrete manholes, catch pits) to be built to Auckland Council standards and verified with As-Built plans before new land titles are issued."
      },
      {
        question: "What is involved in connecting a private sewer line to the public Watercare main?",
        answer: "Public main tie-ins involve site utility location, deep trenching with shoring boxes, coring public manholes or tapping mains, laying Class SN8 PVC pipes, air/water testing, and obtaining a Watercare Certificate of Connection (CoC)."
      },
      {
        question: "Why are pre-cast concrete manholes and catch pits installed on public drainage networks?",
        answer: "Concrete manholes provide maintenance access for public sewer trunks, while street catch pits trap silt and debris before stormwater enters public waterways, meeting Council Code of Practice Chapters 4 and 5."
      },
      {
        question: "How do you manage site safety and utility protection during public main digging?",
        answer: "We utilize trench shoring shields, dewatering pumps, digital utility locators, and hydro-vac non-destructive excavation around live underground power, gas, and telecommunications lines."
      },
      {
        question: "What testing is required on public drainage lines before Council sign-off?",
        answer: "Public drainage lines undergo low-pressure air pressure testing, hydrostatic water head testing, and CCTV mandrel inspections to confirm zero pipe deflection, watertight joints, and true laser falls before council adoption."
      }
    ]
  },
  {
    slug: "rainwater-tank-install",
    title: "Rainwater Tank Installation",
    shortDesc: "Rainwater tank installation and connection for homes, lifestyle blocks, and commercial sites.",
    desc: "Rainwater tank installation for residential, rural, and commercial properties. We handle excavation, tank placement, pipework connections, overflow management, and system setup so your rainwater collection works reliably from day one.",
    features: [
      "Tank excavation and placement",
      "Stormwater capture connections",
      "Overflow and outlet setup",
      "Pump and pipework integration",
      "Residential and commercial installs",
      "Site-ready groundwork"
    ],
    images: [
      { src: "/media/rainwater-tank/rainwater-tank-01.jpeg", alt: "Stormwater detention and rainwater tank installation in Hillsborough", caption: "Hillsborough • Dual Detention & Retention Tank Setup" },
      { src: "/media/rainwater-tank/rainwater-tank-02.jpeg", alt: "Rainwater tank plumbing connections and overflow pipework in Rodney", caption: "Rodney • Heavy-Duty Tank Plumbed Overflow Connection" }
    ],
    contentSections: [
      {
        heading: "Rainwater Tank Installation for Auckland Properties",
        paragraphs: [
          "Installing a rainwater collection or detention system requires solid groundwork, accurate overflow grading, and compliant plumbing connections. Whether you are installing a tank for household water reuse or to satisfy Auckland Council stormwater detention mandates, Craft Civil Contractors manages the complete installation process.",
          "Our team assesses site levels, equipment access, and tank capacities (5,000L to 30,000L). We coordinate [tank site excavation](/services/earthworks-demolition), [structural concrete base pads](/services/concrete-civil) or compacted GAP20 foundations, crane placement, first-flush diverters, and overflow pipework connected to [stormwater drainage networks](/services/private-drainage).",
          "All installations are engineered to withstand Auckland ground movement and heavy weather, ensuring stable tank placement and compliance with the Auckland Unitary Plan Stormwater Management Area Flow (SMAF) rules."
        ]
      },
      {
        heading: "Consented Retention, Detention & Town-to-Tank Top-Up Systems",
        paragraphs: [
          "We install both stormwater retention tanks (for rainwater harvesting and toilet/garden reuse) and stormwater detention tanks (which hold storm surge and release water slowly through a calibrated orifice plate to prevent municipal flooding).",
          "For properties connected to the municipal water supply, we install Watercare Town-to-Tank top-up systems equipped with testable Reduced Pressure Zone (RPZ) backflow prevention valves to safeguard public drinking water. We provide complete installation and testing from excavation to council sign-off."
        ]
      },
      {
        heading: "Auckland Unitary Plan SMAF 1 & 2 Stormwater Detention Mandates",
        paragraphs: [
          "Under the Auckland Unitary Plan, developments in Stormwater Management Area Flow (SMAF 1 and SMAF 2) zones with increased impervious roof and driveway areas must install stormwater detention systems to mitigate peak runoff and protect urban stream networks from erosion. Detention tanks collect heavy downpour volume and release it gradually through a precision-machined stainless steel orifice plate into the municipal stormwater network.",
          "Craft Civil Contractors calculates required detention volumes, installs heavy-duty poly or concrete detention tanks, and fits calibrated orifice restrictor assemblies to ensure full compliance with Auckland Council stormwater consent conditions, with extensive experience across [West Auckland SMAF catchments](/service-areas/west-auckland/new-lynn-henderson) and [Mount Roskill infill developments](/service-areas/central-isthmus/mt-roskill)."
        ]
      },
      {
        heading: "Watercare Town-to-Tank Top-Up & Testable RPZ Backflow Prevention",
        paragraphs: [
          "For households utilizing harvested rainwater for indoor toilet flushing and laundry, dry summer spells require an automated Watercare Town-to-Tank top-up connection. To safeguard the public drinking water supply against cross-contamination, Watercare mandates the installation of a testable Reduced Pressure Zone (RPZ) backflow prevention valve.",
          "We supply, plumb, and commission complete pump and top-up setups—including submerged multi-stage pressure pumps, automatic mains-switching valves, first-flush diverters, and leaf strainers—tested and certified for council compliance."
        ]
      }
    ],
    keyTasks: [
      "Site inspection and stormwater retention/detention planning",
      "Compacted GAP20 metal foundation base construction",
      "Poly and concrete tank placement (5,000L - 30,000L)",
      "Excavation for tank base and overflow trenching",
      "Inlet, outlet, and first-flush diverter plumbing",
      "Downpipe connection and collection leaf strainer setup",
      "Overflow pipe installation to stormwater main or soakhole",
      "Watercare Town-to-Tank top-up plumbing with backflow valves",
      "Submerged water pressure pump installation and testing",
      "Final connection inspection and site clean-up"
    ],
    whyTrustUs: [
      "We have 15+ years of practical civil contracting and tank installation experience",
      "We ensure full compliance with Auckland Unitary Plan stormwater detention rules",
      "We construct compacted GAP20 foundations for stable, unyielding tank support",
      "We install testable backflow valves for Watercare Town-to-Tank connections",
      "Our careful level checks maintain accurate overflow pipe falls",
      "We offer competitive rates for residential, rural, and commercial installations",
      "Our communication stays clear throughout planning and completion"
    ],
    faqs: [
      {
        question: "What is the difference between a stormwater retention tank and a detention tank?",
        answer: "A retention tank stores rainwater for household reuse (garden watering, toilet flushing). A detention tank holds stormwater during heavy rain and releases it slowly through a controlled orifice into the public main to prevent city flooding."
      },
      {
        question: "Do I need a building consent for a rainwater tank in Auckland?",
        answer: "Building consent is required if the tank connects to internal property plumbing (toilets, laundry) or functions as a mandatory stormwater detention system under your building consent."
      },
      {
        question: "What are Auckland Council SMAF 1 and SMAF 2 stormwater detention requirements?",
        answer: "SMAF 1 and SMAF 2 zones mandate detention tanks with specific storage volumes and orifice plate release rates to limit stormwater discharge velocity into sensitive Auckland stream catchments."
      },
      {
        question: "What is a Watercare Town-to-Tank connection and what backflow prevention is required?",
        answer: "Town-to-Tank connections allow public water supply to top up rainwater tanks during dry periods. Watercare mandates a testable Reduced Pressure Zone (RPZ) backflow prevention valve to protect public drinking water."
      },
      {
        question: "What foundation preparation (GAP20) is required for heavy rainwater tanks?",
        answer: "Tanks weighing up to 30,000L require excavation to solid ground, placement of compacted GAP20 aggregate metal or a reinforced concrete slab to ensure a level, unyielding base."
      },
      {
        question: "How do first-flush diverters and leaf strainers keep harvested water clean?",
        answer: "Leaf strainers catch large debris, while first-flush diverters isolate the initial dirty roof runoff during rainfall, ensuring only clean rainwater enters the storage tank."
      }
    ]
  },
  {
    slug: "wastewater-systems",
    title: "Wastewater & Sewer Systems",
    shortDesc: "Wastewater pipework, sewer connections, upgrades, and compliant site infrastructure.",
    desc: "Wastewater and sewer system installation for residential, commercial, and development sites. We deliver compliant pipework, sewer connections, inspection points, and supporting infrastructure with a focus on reliability, flow performance, and long-term durability.",
    features: [
      "Wastewater pipe installation",
      "Sewer and council connections",
      "System upgrades and replacements",
      "Inspection points and chambers",
      "Site compliance support",
      "Residential and commercial solutions"
    ],
    images: [
      { src: "/media/wastewater-systems/wastewater-01.jpeg", alt: "Class SN8 sewer lateral installation in Manukau", caption: "Manukau • Class SN8 Heavy-Wall Sewer Lateral Installation" },
      { src: "/media/wastewater-systems/wastewater-02.jpeg", alt: "Inspection chamber construction and wastewater line in Franklin", caption: "Franklin • Wastewater Inspection Chamber & Boundary Tie-In" }
    ],
    contentSections: [
      {
        heading: "Wastewater Pipework, Sewer Connections & System Upgrades",
        paragraphs: [
          "Sanitary wastewater systems require durable pipe materials, laser-verified falls, and watertight joints to prevent ground contamination and structural blockages. Craft Civil Contractors installs, repairs, and upgrades wastewater networks for new subdivisions, residential builds, and commercial sites.",
          "Our sewer installation services include trench excavation, aggregate bedding, laying Class SN4 or SN8 PVC sewer pipes, constructing precast inspection chambers, and coordinating [certified sewer drainlaying](/services/drainlaying) with [public drainage main extensions](/services/public-drainage)."
        ]
      },
      {
        heading: "Sewer Network Services, Watercare CoC Tie-Ins & On-Site Wastewater Systems",
        paragraphs: [
          "We construct reticulated sewer pipelines, connect properties to public trunks, and install Aerated Wastewater Treatment Systems (AWTS) for unreticulated lifestyle blocks across Auckland.",
          "For damaged sewer lines, we locate faults using digital diagnostic equipment and perform targeted pipe replacements to minimize property disruption. Every sewer line is verified with air or hydrostatic water pressure testing before securing Watercare Certificate of Connection (CoC) sign-offs."
        ]
      },
      {
        heading: "Earthenware Sewer Lateral Replacement & Structural Upgrades",
        paragraphs: [
          "Cracked, brittle earthenware pipes in older Auckland suburbs allow groundwater ingress during storms (overloading city treatment networks) and leak foul sewage into subsoils during dry weather. We excavate failed earthenware laterals, prepare compacted GAP20 aggregate bedding, and install heavy-wall Class SN4 or Class SN8 PVC-U foul sewer pipes with flexible rubber ring joints (RRJ) that absorb ground shifting.",
          "Every new sewer line is graded to a precise laser fall of 1:60 (1.65%), installed with accessible inspection openings (IOs) and boundary disconnect chambers for straightforward ongoing maintenance."
        ]
      },
      {
        heading: "Aerated Wastewater Treatment Systems (AWTS) for Unreticulated Sites",
        paragraphs: [
          "For rural properties, lifestyle blocks, and unreticulated developments outside Auckland's municipal sewer network, we install advanced secondary Aerated Wastewater Treatment Systems (AWTS). These multi-chamber aerobic plants purify household blackwater and greywater into treated, odourless effluent.",
          "We engineer and install subsurface pressure-compensating dripper lines and shallow land disposal fields tailored to the slow absorption rates of Auckland's heavy clay soils, ensuring full compliance with regional discharge consents."
        ]
      }
    ],
    keyTasks: [
      "Installation of heavy-wall PVC sewer lateral pipes",
      "Watercare sewer network connection and tie-in works",
      "Aerated Wastewater Treatment System (AWTS) installation",
      "Construction of concrete sewer inspection chambers",
      "Effluent disposal field trenching and pipe laying",
      "Sewer main replacement and undersized pipe upgrades",
      "Trench shoring, bedding, and backfill compaction",
      "Air and hydrostatic pressure testing for sewer leak detection",
      "Smoke testing for sewer gas fault diagnostics",
      "Documentation support for Watercare Certificate of Connection (CoC)"
    ],
    whyTrustUs: [
      "We handle Watercare Certificate of Connection (CoC) requirements for sewer tie-ins",
      "Our team plans around site levels, utility locations, and ground conditions",
      "We use heavy-wall PVC pipework and pre-cast concrete inspection chambers",
      "We perform air and water pressure testing before site handover",
      "Our experience covers urban reticulated sewer mains and rural AWTS treatment systems",
      "We provide clear pricing and structured project execution"
    ],
    faqs: [
      {
        question: "What is an Aerated Wastewater Treatment System (AWTS) and how does it work?",
        answer: "An AWTS is an advanced secondary treatment plant used on unreticulated rural sites to purify domestic wastewater through multi-chamber aeration before safely discharging clean effluent into land disposal fields."
      },
      {
        question: "How does Craft Civil obtain a Watercare Certificate of Connection (CoC) for new sewer lines?",
        answer: "We handle utility location, trench excavation, main tapping, pipe laying, air testing, and final Watercare inspection sign-off to issue your Certificate of Connection."
      },
      {
        question: "What is involved in replacing an old earthenware sewer line with modern PVC?",
        answer: "We locate existing sewer laterals, excavate trenches carefully around utilities, lay heavy-wall Class SN4/SN8 PVC pipes with rubber ring joints, verify fall, and connect to inspection chambers."
      },
      {
        question: "What is an effluent land disposal field and how is it engineered for clay soils?",
        answer: "Effluent disposal fields utilize dripper lines or shallow trenches in topsoil to safely absorb treated wastewater, engineered to match soil absorption rates under Auckland Council discharge consents."
      },
      {
        question: "How do hydrostatic water and air pressure tests detect sewer line leaks?",
        answer: "New sewer installations are sealed with plugs and pressurized with air or water to verify zero pressure drop before backfilling, ensuring watertight performance."
      },
      {
        question: "What gradient fall is required for sanitary sewer laterals (NZBC G13)?",
        answer: "NZ Building Code Clause G13 requires foul sewer lines to be laid at a minimum gradient of 1:60 (1.65% fall) to maintain self-cleansing velocity and prevent solids from settling in the pipe."
      }
    ]
  },
  {
    slug: "drain-unblocking",
    title: "Drain Unblocking",
    shortDesc: "Fast drain clearing, CCTV inspections, and hydro-jetting for blocked systems.",
    desc: "Fast, effective relief for blocked drains. We use CCTV and hydro-jetting technology to diagnose and clear blockages quickly, with emergency response available when needed.",
    features: [
      "Emergency 24/7 callouts",
      "High-pressure water jetting",
      "CCTV inspections",
      "Root cutting",
      "Grease trap cleaning",
      "Preventive maintenance"
    ],
    images: [
      { src: "/media/asset-12.jpg", alt: "High pressure hydro-jetting and CCTV drain inspection unit in Auckland", caption: "Auckland • 4000 PSI Hydro-Jetting & CCTV Inspection Unit" }
    ],
    contentSections: [
      {
        heading: "Emergency Drain Unblocking with CCTV and Hydro-Jetting",
        paragraphs: [
          "A blocked drain or overflowing sewer gully needs fast, accurate diagnosis before digging up your lawn or driveway. Craft Civil Contractors clears blocked drains across Auckland using high-definition CCTV video inspections to pinpoint the obstruction before coordinating any [licensed drainlaying repairs](/services/drainlaying).",
          "Using 4,000 PSI high-pressure hydro-jetting, sonar-equipped CCTV cameras, and mechanical root cutters, we clear grease, silt, and heavy tree root intrusions without damaging existing pipework.",
          "Dispatched directly from our 110 Carlton Street headquarters, our operators guarantee rapid 15-minute emergency response across [Hillsborough, Onehunga and Central Auckland](/service-areas/central-isthmus/hillsborough). We clean each pipe, joint, and outlet systematically, reducing future blockage risks. When CCTV reveals collapsed earthenware pipes, we coordinate permanent [private drain replacement](/services/private-drainage)."
        ]
      },
      {
        heading: "CCTV Drain Camera Inspections & Tree Root Clearing",
        paragraphs: [
          "Repeated blockages are usually caused by underlying structural defects such as cracked earthenware joints, bellies (sags), or invasive tree roots. Our high-resolution CCTV camera surveys inspect the full pipe interior, recording exact defect depths and locations to determine whether hydro-jetting or localized pipe replacement is required.",
          "We offer 24/7 emergency blockage removal for overflowing gully traps, backed-up toilets, and commercial grease traps. All work is conducted safely, restoring full flow performance to your drainage network."
        ]
      },
      {
        heading: "Overflowing Sewer Gully Traps & 24/7 Emergency Triage",
        paragraphs: [
          "An overflowing sewer gully trap or backed-up toilet is a serious biohazard requiring immediate attention to prevent raw sewage from flooding home interiors or contaminating outdoor living areas. When a blockage causes a gully surcharge, stop using all household water fixtures immediately and call our 24/7 emergency response team.",
          "Our operators arrive with dedicated jetting units and diagnostic equipment to clear the downstream blockage rapidly, sanitize the surcharge area, and evaluate whether structural pipe collapse or root intrusion caused the backup."
        ]
      },
      {
        heading: "HD CCTV Sonar Video Surveys & Insurance Claim Documentation",
        paragraphs: [
          "Clearing a drain only solves half the problem; identifying why it blocked is essential to prevent repeated callout costs. We deploy high-definition pan-and-tilt CCTV drain cameras equipped with 512Hz digital sonar locating sondes.",
          "The camera travels through the pipe interior, pinpointing exact defect depths, structural cracks, ground sags (bellies), and invasive root masses. We provide customers and insurance assessors with timestamped video footage, digital condition reports, and clear fixed-scope quotes for any necessary permanent [drainlaying repairs](/services/drainlaying)."
        ]
      }
    ],
    keyTasks: [
      "4,000 PSI high-pressure water jetting",
      "CCTV video pipe camera inspections and sonar line locating",
      "Mechanical tree root cutting and root mass removal",
      "Sewer main and stormwater line blockage clearing",
      "Gully trap surcharge and toilet backup unblocking",
      "Commercial grease trap and sludge chamber cleaning",
      "Kitchen, laundry, and bathroom drain clearing",
      "Preventive maintenance drain flushing",
      "CCTV video report preparation for insurance claims",
      "Emergency 24/7 blockage response"
    ],
    whyTrustUs: [
      "We utilize 4,000 PSI hydro-jetting and high-resolution CCTV video cameras",
      "Our licensed team delivers safe, compliant drainage clearance",
      "We provide prompt 24/7 emergency response for urgent sewer surcharges",
      "Our hydro-jetting scours pipe walls clean without structural pipe damage",
      "We deliver long-term solutions, providing honest site advice",
      "Our pricing is transparent and competitive across Auckland"
    ],
    faqs: [
      {
        question: "How does a 4,000 PSI hydro-jetter clear blockages without damaging pipes?",
        answer: "Hydro-jetting uses high-pressure water streams to scour grease, heavy silt, and tree roots completely off internal pipe walls, restoring 100% full bore capacity without scraping or damaging the pipe."
      },
      {
        question: "How does a CCTV drain camera survey pinpoint the exact location of a blockage?",
        answer: "A specialized HD camera is inserted into the pipe to visually inspect the line, identify root intrusion, cracks, or sags, and pinpoint the exact depth and location using digital sonar locator beacons."
      },
      {
        question: "What factors determine the cost of drain unblocking in Auckland?",
        answer: "Pricing depends on blockage severity, pipe depth, location, and equipment needed. Standard callouts cover basic clearing, while severe root choke or collapsed line clearing utilizes 4,000 PSI jetting and CCTV packages."
      },
      {
        question: "What should I do if my gully trap overflows with sewage?",
        answer: "Avoid flushing toilets or running taps to prevent indoor flooding. Contact our 24/7 emergency response team immediately to clear the sewer surcharge and inspect the main line."
      },
      {
        question: "Why do tree roots infiltrate older earthenware pipe joints?",
        answer: "Older earthenware pipes have mortared joints that crack over time. Tree roots enter seeking moisture, growing into dense root masses. We use mechanical root cutters and hydro-jetting to clear them."
      },
      {
        question: "Do you provide CCTV video footage for home insurance claims?",
        answer: "Yes. Following a CCTV camera inspection, we supply complete digital video footage and a written defect report documenting root intrusion, cracked pipework, or collapsed earthenware for insurance assessment."
      }
    ]
  },
  {
    slug: "earthworks-demolition",
    title: "Earthworks & Demolition",
    shortDesc: "Site prep, trenching, excavation, and demolition for residential and commercial jobs.",
    desc: "Professional site preparation services. Our operators handle excavation, trenching, land clearing, and demolition so your project starts on a properly prepared site.",
    features: [
      "Site excavation",
      "Building demolition",
      "Land clearing",
      "Trenching",
      "Retaining wall prep",
      "Driveway excavation"
    ],
    images: [
      { src: "/media/earthworks/earthworks-01.jpeg", alt: "Civil earthworks site grading and trenching in Papakura", caption: "Papakura • Building Pad Levelling & Trench Excavation" }
    ],
    contentSections: [
      {
        heading: "Site Preparation, Excavation & Earthworks for Auckland Projects",
        paragraphs: [
          "Craft Civil Contractors provides comprehensive site preparation, [private drainage trenching](/services/private-drainage), and earthworks across Auckland for residential developments, subdivisions, and commercial civil projects. Our experienced operators manage site scraping, bulk excavation, cut-and-fill operations, building platform levelling, and foundation trenching.",
          "We evaluate ground levels, soil conditions, and site access before digging. Implementing Auckland Council GD05 erosion and sediment controls—including stabilized aggregate entrance tracking pads—we ensure your building pad or driveway subgrade is properly prepared before [concrete driveway pours](/services/concrete-civil)."
        ]
      },
      {
        heading: "Building Platform Grading, Sub-Base Compaction & Controlled Demolition",
        paragraphs: [
          "Before site construction begins, existing structures, old concrete slabs, timber decks, and overgrown vegetation must be cleared safely. We handle site clearing and controlled demolition across Auckland, separating recyclable materials and carting clean fill.",
          "Our excavation process incorporates Scala penetrometer soil bearing capacity testing, GAP65 sub-base metal placement, precision trenching for [certified drainlaying](/services/drainlaying) and pipe installations, and controlled roller compaction. We manage spoil removal and clean fill cartage, leaving level, unyielding building platforms."
        ]
      },
      {
        heading: "Auckland Council GD05 Erosion & Sediment Control Protocols",
        paragraphs: [
          "All land-disturbing activities in Auckland must comply with Auckland Council Guideline Document GD05 to prevent sediment and muddy runoff from contaminating public stormwater drains and waterways. We construct stabilized aggregate entrance tracking pads using coarse crushed rock, install geotextile silt fencing, and set up decanting earth bunds before commencing major site cuts.",
          "Our site management ensures all excavation activities remain fully compliant throughout both standard operations and the restricted Auckland winter earthworks season."
        ]
      },
      {
        heading: "Building Platform Cuts, Scala Penetrometer Testing & Sub-Base Compaction",
        paragraphs: [
          "A stable building platform is fundamental to preventing structural slab cracking and foundation subsidence. We execute precision cut-and-fill site balancing to establish true engineering levels while minimizing costly clean fill spoil cartage.",
          "Subgrades are tested on-site using Scala dynamic cone penetrometer equipment to verify required soil bearing capacity (kPa). We place and roll GAP65 crushed metal sub-base layers to strict compaction densities, leaving unyielding, engineer-ready house pads for [concrete slab pours](/services/concrete-civil)."
        ]
      }
    ],
    keyTasks: [
      "Bulk excavation and cut-and-fill site balancing",
      "Building platform (house pad) grading and compaction",
      "Auckland Council GD05 stabilized entrance tracking pad setup",
      "Scala penetrometer soil bearing capacity testing",
      "Foundation trenching and retaining wall excavation",
      "GAP65 crushed metal sub-base placement and compaction",
      "Site clearing and vegetation removal",
      "Controlled residential structure and concrete slab demolition",
      "Clean fill excavation and spoil removal cartage",
      "Subgrade excavation for driveways and paved areas"
    ],
    whyTrustUs: [
      "We bring 15+ years of practical civil contracting and earthmoving experience",
      "Our site operations comply with Auckland Council GD05 sediment control guidelines",
      "We perform precise level checks and Scala penetrometer soil compaction testing",
      "Our operators run modern 5t-20t excavators, skid steers, and rollers",
      "We check utility locations prior to digging to ensure site safety",
      "We provide clear, competitive site excavation quotes"
    ],
    faqs: [
      {
        question: "What is Auckland Council GD05 sediment control and why are tracking pads mandatory?",
        answer: "GD05 is Auckland Council's guideline for land-disturbing activities. It mandates silt fencing and stabilized aggregate entrance tracking pads to prevent soil and mud from washing into public stormwater networks."
      },
      {
        question: "What is cut and fill site balancing and how does it reduce house pad preparation costs?",
        answer: "Cut and fill involves excavating earth from higher ground on a site ('cut') and placing/compacting it into lower areas ('fill') to create a level building platform without importing excessive fill."
      },
      {
        question: "When do earthworks require resource consent vs building consent in Auckland?",
        answer: "Resource consent is required under the Unitary Plan if earthwork volumes or areas exceed permitted thresholds or occur on steep slopes (>15°). Building consent covers structural foundations and retaining walls."
      },
      {
        question: "What is the Auckland Council 'earthworks season' (1 Oct – 30 Apr) restriction?",
        answer: "Bulk earthworks are restricted during wet winter months to prevent severe erosion and sediment runoff. Major land-disturbing activities are planned within the official 1 October to 30 April season."
      },
      {
        question: "What is a Scala penetrometer test and why is soil bearing capacity verified?",
        answer: "A Scala penetrometer test measures subgrade soil density and load-bearing capacity, confirming that building platforms meet structural engineering requirements before pouring concrete foundations."
      },
      {
        question: "How do you manage spoil removal and clean fill cartage?",
        answer: "We excavate, load, and cart surplus soil to licensed Auckland clean fill facilities, providing tipping dockets and tracking manifests to verify environmental compliance."
      }
    ]
  },
  {
    slug: "concrete-civil",
    title: "Concrete & Civil Work",
    shortDesc: "Driveways, paths, foundations, and concrete civil works built to last.",
    desc: "High-quality concrete solutions for functional and aesthetic needs. We pour driveways, paths, and foundations that are built for durability and a sharp finished look.",
    features: [
      "Driveways and paths",
      "Foundations",
      "Retaining walls",
      "Exposed aggregate",
      "Decorative concrete",
      "Concrete repairs"
    ],
    images: [
      { src: "/media/asset-14.jpg", alt: "Concrete driveway pour and reinforced slab preparation in Auckland", caption: "Auckland • Reinforced Vehicle Crossing & Concrete Pour" }
    ],
    contentSections: [
      {
        heading: "Concrete Driveways, Slabs & Vehicle Crossings",
        paragraphs: [
          "Durable concrete begins with a properly compacted sub-base, accurate boxing, and correctly tied steel reinforcement. Craft Civil Contractors pours residential driveways, commercial slabs, and footpaths across Auckland, integrating [driveway channel drainage](/services/private-drainage) to manage surface runoff effectively.",
          "We construct Auckland Transport approved vehicle crossings complying with Unitary Plan Chapter E27 and TDM Drawing VX0102 standards, supporting [subdivision driveways across Papakura, Drury and South Auckland](/service-areas/south-auckland/papakura-drury). Every project includes [subgrade earthworks compaction](/services/earthworks-demolition), base metal preparation, and certified 25MPa to 40MPa concrete mixes tailored to traffic loads."
        ]
      },
      {
        heading: "Exposed Aggregate Driveways, Steel Mesh Reinforcing & Kerb Construction",
        paragraphs: [
          "Our concrete installation includes excavation, GAP20 base metal compaction, Damp-Proof Membrane (DPM) polythene laying, SE62/SE72 steel mesh tying, pouring, and decorative finishing for civil and residential applications.",
          "We pour exposed aggregate driveways, plain broom-finished concrete, kerb and layby crossings, and dished channels, including full [drainlaying trenching reinstatement](/services/drainlaying) following pipe repairs. Every pour adheres to NZS 3109 structural concrete standards, incorporating expansion joints to prevent cracking and surface damage."
        ]
      },
      {
        heading: "Auckland Transport TDM VX0102 Vehicle Crossing Approvals & Compliance",
        paragraphs: [
          "Constructing or altering a driveway vehicle crossing over a public berm or footpath requires formal Auckland Transport approval under Unitary Plan Chapter E27 and Transport Design Manual (TDM) Drawing VX0102 standards. Crossings must be engineered with reinforced layby kerb cuts, specific transition gradients, and heavy-duty concrete to withstand repeated vehicle wheel loading without damaging public footpath assets.",
          "Craft Civil Contractors manages the entire process—from submitting Corridor Access Requests (CAR) and Traffic Management Plans (TMP) to excavation, boxing, AT pre-pour inspections, and final smooth concrete finishing."
        ]
      },
      {
        heading: "NZS 3109 Structural Concrete, SE62 Steel Mesh & Decorative Finishes",
        paragraphs: [
          "Every structural driveway and commercial slab we pour adheres to NZS 3109 specifications. We lay Damp-Proof Membrane (DPM) polythene moisture barriers over compacted GAP20 sub-base metal, tie SE62 or SE72 structural ductile steel mesh on reinforcing chairs, and pour certified 25MPa to 40MPa ready-mix concrete.",
          "We offer durable plain broom finishes, non-slip acid-wash concrete, and premium exposed aggregate driveways washed and sealed with UV-resistant acrylic sealers to protect against moss growth and vehicle oil stains."
        ]
      }
    ],
    keyTasks: [
      "Auckland Transport approved vehicle crossing construction (TDM VX0102)",
      "Exposed aggregate and decorative concrete driveway pours",
      "Residential house slab and commercial floor slab construction",
      "Footpath, walkway, and patio concrete installation",
      "Kerb and channel, layby, and dished channel construction",
      "NZS 3109 compliant formwork and SE62/SE72 steel mesh tying",
      "Damp-Proof Membrane (DPM) polythene laying and subgrade compaction",
      "Concrete saw cutting, expansion joint sealing, and curing",
      "Concrete cutting, breaking, and old slab removal",
      "Concrete repair, resurfacing, and surface restoration"
    ],
    whyTrustUs: [
      "We construct Auckland Transport approved vehicle crossings meeting TDM VX0102 standards",
      "All structural concrete pours comply with NZS 3109 construction specifications",
      "We place SE62/SE72 steel mesh reinforcement to prevent cracking under load",
      "We offer exposed aggregate, plain broom, and decorative concrete finishes",
      "Our team manages subgrade metal compaction and DPM moisture barrier installation",
      "We maintain clear communication from box formwork to final sealing",
      "We provide competitive pricing without lowering material or pour quality"
    ],
    faqs: [
      {
        question: "What is required for an Auckland Transport (AT) vehicle crossing approval (TDM VX0102)?",
        answer: "Driveway vehicle crossings over public footpaths require Auckland Transport approval under Unitary Plan E27, adhering to TDM Drawing VX0102 specifications for width, gradient, layby kerb cuts, and concrete strength."
      },
      {
        question: "How much does an exposed aggregate concrete driveway cost per m² in Auckland?",
        answer: "Exposed aggregate driveways generally range from $120 to $200 per m² depending on excavation, base metal metal depth, mesh reinforcement, and decorative aggregate selection."
      },
      {
        question: "Why is SE62 steel mesh reinforcement used in concrete driveway pours (NZS 3109)?",
        answer: "SE62 steel mesh reinforces concrete against heavy vehicle loads, distributing weight evenly and controlling shrinkage cracking to ensure long-term structural durability."
      },
      {
        question: "How often should an exposed aggregate driveway be sealed to maintain appearance?",
        answer: "Exposed aggregate driveways should be sealed after curing and resealed every 2 to 3 years to protect the aggregate from oil stains, moss growth, and UV fading."
      },
      {
        question: "How does Damp-Proof Membrane (DPM) polythene protect concrete house slabs?",
        answer: "DPM polythene laid under concrete slabs blocks ground moisture from rising through the porous concrete into building interiors, complying with NZS 3604 building standards."
      },
      {
        question: "When should expansion saw cuts be made in newly poured concrete driveways?",
        answer: "Expansion saw cuts should be cut within 12 to 24 hours of pouring to control shrinkage cracking, spaced at intervals of roughly 24 to 30 times the slab thickness."
      }
    ]
  },
  {
    slug: "plumbing",
    title: "Plumbing",
    shortDesc: "Reliable plumbing for new builds, upgrades, repairs, and renovations.",
    desc: "Full-service plumbing for renovations and new builds. Our team delivers reliable, code-compliant installations, upgrades, and repairs for residential and commercial projects.",
    features: [
      "New installations",
      "Hot water systems",
      "Bathroom renovations",
      "Pipe repairs",
      "Water pressure improvements",
      "Gas fitting"
    ],
    images: [
      { src: "/media/asset-15.jpg", alt: "Sub-surface commercial water main installation and PE pipework in Auckland", caption: "Auckland • Sub-Surface Water Supply & PE Pipework" }
    ],
    contentSections: [
      {
        heading: "Commercial Plumbing & Water Main Infrastructure",
        paragraphs: [
          "Craft Civil Contractors provides expert commercial and civil plumbing services in Auckland. Our team specializes in [Watercare water main tapping](/services/public-drainage), Reduced Pressure Zone (RPZ) backflow prevention, Pressure Reducing Valves (PRVs), and sub-surface water supply line installations for residential developments and commercial sites.",
          "We handle new PE pipework installations and pipe replacements with minimal site disruption. From Watercare main water supply tapping to underground supply line trenching, our experienced plumbers deliver code-compliant infrastructure coordinated alongside [wastewater systems](/services/wastewater-systems)."
        ]
      },
      {
        heading: "Underground Water Main Repairs & RPZ Backflow Prevention Testing",
        paragraphs: [
          "Underground water main leaks and pressure issues require fast, systematic diagnosis and repair. We use digital line locating, careful excavation, and electrofusion PE pipe welding to restore full water main performance.",
          "Whether you need a commercial fit-out, water meter installation, RPZ backflow valve testing, or underground supply line coordination with [boundary sewer drainlaying](/services/drainlaying), Craft Civil Contractors offers professional service complying with AS/NZS 3500.1 standards."
        ]
      },
      {
        heading: "Commercial Water Mains, PE Pipe Electrofusion & Watercare Tapping",
        paragraphs: [
          "Supplying potable water to commercial developments, subdivisions, and multi-unit residential sites requires heavy-duty Polyethylene (PE100) pipework engineered to withstand high municipal pressure. Craft Civil Contractors performs certified electrofusion and butt welding to create seamless, leak-proof pipe joints resistant to ground movement and corrosion.",
          "We manage live Watercare water main tapping, commercial water meter installations, sub-surface trenching, and pressure-reducing valve (PRV) setups to regulate incoming line pressure."
        ]
      },
      {
        heading: "Reduced Pressure Zone (RPZ) Backflow Prevention & Annual IQP Certification",
        paragraphs: [
          "Under the New Zealand Building Code (Clause G12) and AS/NZS 3500.1, commercial properties, industrial facilities, and properties with rainwater harvesting top-ups must have testable Reduced Pressure Zone (RPZ) backflow prevention devices installed to protect Auckland's drinking water network from back-siphonage.",
          "We install, test, and commission RPZ valves, double check detector assemblies, and provide annual IQP (Independently Qualified Person) test certificates submitted directly to Auckland Council for Building Warrant of Fitness (BWOF) compliance."
        ]
      }
    ],
    keyTasks: [
      "Commercial and civil main water line installations",
      "Watercare main tapping and commercial water meter setup",
      "Reduced Pressure Zone (RPZ) backflow prevention valve installation",
      "Annual backflow prevention testing and IQP compliance sign-off",
      "Underground PE water pipe electrofusion and butt welding",
      "Pressure Reducing Valve (PRV) installation and calibration",
      "Sub-surface leak detection and main supply line replacement",
      "Hot water system piping and commercial plumbing fit-outs",
      "Pipe trenching, bedding, and backfilling for water mains",
      "System hydrostatic pressure testing and compliance sign-off"
    ],
    whyTrustUs: [
      "We utilize experienced specialists for civil and commercial plumbing",
      "We install and test RPZ backflow preventers for Watercare compliance",
      "We perform electrofusion PE pipe welding for durable water mains",
      "Our work complies with Auckland Council and Watercare standards (AS/NZS 3500.1)",
      "We deliver clear pricing and prompt project execution",
      "We support projects from initial site assessment to final compliance sign-off"
    ],
    faqs: [
      {
        question: "What is a Reduced Pressure Zone (RPZ) backflow valve and why is annual testing mandatory?",
        answer: "A Reduced Pressure Zone (RPZ) valve prevents contaminated water from back-siphoning into the public Watercare drinking water supply. Annual testing by an IQP inspector is legally mandated under AS/NZS 3500.1."
      },
      {
        question: "How is a property connected to the main Watercare drinking water supply line?",
        answer: "We handle utility location, trench excavation, Watercare tapping approvals, electrofusion PE pipe welding, pressure testing, and final connection to commercial or residential water meters."
      },
      {
        question: "What is an IQP (Independently Qualified Person) backflow compliance certificate?",
        answer: "An IQP certificate is a formal compliance document issued after testing backflow devices, submitted to Auckland Council to verify drinking water safety."
      },
      {
        question: "Why are Pressure Reducing Valves (PRVs) installed on water supply lines?",
        answer: "PRVs regulate high incoming municipal water pressure down to safe operating levels, protecting interior plumbing fixtures, water heaters, and pipe joints from pressure damage."
      },
      {
        question: "How does electrofusion PE pipe welding create leak-proof underground water mains?",
        answer: "Electrofusion uses electrical current to melt PE pipe fittings together, fusing the plastic into a seamless, pressure-rated joint resistant to ground movement."
      },
      {
        question: "What pipe materials are specified for commercial underground water mains?",
        answer: "High-Density Polyethylene (HDPE PE100) is specified for modern underground water mains due to its flexibility, high pressure rating (PN16), and jointless electrofusion welding."
      }
    ]
  },
  {
    slug: "drainlaying",
    title: "Drainlaying",
    shortDesc: "Certified drainlaying for subdivisions, new builds, and infrastructure upgrades.",
    desc: "Expert drainlaying services for new subdivisions and property developments. Certified and experienced in all aspects of drainlaying, from new installs to alterations and relining.",
    features: [
      "New drain installs",
      "Sewer connections",
      "Septic tanks",
      "Pipe relining",
      "Drain alterations",
      "Council inspections"
    ],
    images: [
      { src: "/media/asset-07.jpg", alt: "Certified drainlaying crew installing stormwater and sewer pipes in Auckland", caption: "Auckland • Laser-Guided Drainlaying & Council Sign-Off" }
    ],
    contentSections: [
      {
        heading: "Certified Drainlaying Services for Auckland Homes & Developments",
        paragraphs: [
          "As a certified drainlayer in Auckland, Craft Civil Contractors delivers complete stormwater and wastewater pipe installations for new residential builds, subdivisions, and property renovations. From initial council GeoMaps checks to final connection and testing, we ensure every drainage network is built for long-term flow performance and full regulatory compliance, including [public sewer main tie-ins](/services/public-drainage).",
          "Our drainlayers manage the entire installation process—laser-guided trenching, compacted GAP20 aggregate bedding, laying Class SN4 or SN8 PVC-U pipes with rubber ring joints (RRJ), and careful backfill compaction. We test every line thoroughly with low-pressure air or hydrostatic water pressure tests before trench closure.",
          "We issue Producer Statement PS3 (Construction) documentation for Auckland Council Building Consent sign-offs, certifying that [private drainage systems](/services/private-drainage) and sewer laterals comply with NZ Building Code E1 and G13."
        ]
      },
      {
        heading: "Laser Gradient Alignment, Hydrostatic Testing & As-Built Certification",
        paragraphs: [
          "Proper drainlaying prevents common issues like leaking earthenware joints, ground sags, and inadequate gradients that cause repeated blockages. Whether you are subdividing land or upgrading existing pipework, our registered team integrates new drainage smoothly with existing underground infrastructure and utilities.",
          "We utilize pipe lasers for precise gradient levelling (1:60 sewer falls, 1:100 stormwater falls), hydrostatic water testing, and low-pressure air testing. We prepare detailed As-Built CAD plans for Council GeoMaps submission, providing clear cost advice based on site scope."
        ]
      },
      {
        heading: "Auckland Council Producer Statement PS3 (Construction) Compliance Framework",
        paragraphs: [
          "A Producer Statement PS3 (Construction) is the formal legal document issued by a registered PGDB Certifying Drainlayer confirming that all sub-surface drainage has been installed strictly to approved building consent plans, Auckland Council engineering standards, and New Zealand Building Code Clauses E1 (Surface Water) and G13 (Foul Water). Without a signed PS3 and verified test records, Auckland Council will not issue your Code Compliance Certificate (CCC).",
          "Craft Civil Contractors oversees the complete compliance sequence—conducting on-site low-pressure air testing, hydrostatic water head testing, organizing council visual inspections, and lodging all signed producer statements alongside scaled As-Built plans."
        ]
      },
      {
        heading: "Residential New Build Drainage Packages & Infill Subdivision Infrastructure",
        paragraphs: [
          "New residential builds and multi-unit subdivisions demand seamless coordination between earthworks, foundations, and underground drainage networks. Before excavating, our certifying drainlayers check public connection depths and boundary offsets using Auckland Council GeoMaps GIS data.",
          "We handle the complete physical drainage package: trenching to solid bearing, laying Class SN4 PVC under soft landscaping, laying Class SN8 heavy-wall PVC under driveways, setting laser falls (1:60 sewer, 1:100 stormwater), installing precast concrete inspection chambers, and completing live [public sewer and stormwater main tie-ins](/services/public-drainage)."
        ]
      }
    ],
    keyTasks: [
      "PGDB certified sanitary sewer and stormwater drainlaying",
      "Laser-guided trench grading (1:60 to 1:100 falls)",
      "Class SN4 and SN8 PVC-U pipe laying and RRJ jointing",
      "GAP20 aggregate pipe bedding and trench compaction",
      "Producer Statement PS3 (Construction) documentation for Council",
      "Hydrostatic water testing and low-pressure air pipe testing",
      "As-built CAD drainage plan preparation for Council GIS",
      "Pre-cast concrete manhole and inspection chamber installation",
      "Subsoil Novacoil and French drain laying",
      "Final system commissioning and Code Compliance Certificate (CCC) support"
    ],
    whyTrustUs: [
      "We issue Producer Statement PS3 documentation for Auckland Council Consent sign-offs",
      "Our work complies with NZ Building Code E1/G13 and PGDB drainlaying regulations",
      "We utilize pipe lasers for exact 1:60 sewer and 1:100 stormwater gradient falls",
      "We prepare accurate As-Built CAD plans for Council GeoMaps GIS submission",
      "Our modern equipment supports efficient, clean, and compliant drainlaying",
      "We provide transparent pricing with no hidden project costs",
      "Our experienced team solves complex drainage challenges with practical methods"
    ],
    faqs: [
      {
        question: "What is a Producer Statement PS3 and why is it needed for Building Consent?",
        answer: "A Producer Statement PS3 (Construction) is a formal compliance statement signed by a qualified drainlayer confirming that drainage pipework was installed according to approved plans and tested to NZ Building Code E1/G13 standards."
      },
      {
        question: "What is the difference between a Certifying Drainlayer and a Licensed Drainlayer?",
        answer: "A Certifying Drainlayer holds the highest PGDB registration, legal authority to design, test, and sign off PS3 producer statements for Council. A Licensed Drainlayer performs installation under certifying supervision."
      },
      {
        question: "Why is exact laser pipe gradient (fall) so critical during drainlaying?",
        answer: "Laser-guided grading ensures precise fall (typically 1:60 for sewer, 1:100 for stormwater), maintaining self-cleansing water velocity that prevents solids from settling and clogging pipes."
      },
      {
        question: "How are new PVC-U drainage pipes tested before backfilling?",
        answer: "Newly laid lines undergo low-pressure air testing and hydrostatic water pressure testing to verify watertight joints before backfilling with GAP20 aggregate bedding."
      },
      {
        question: "What is the difference between Class SN4 and Class SN8 PVC drainage pipes?",
        answer: "Class SN4 PVC is suitable for standard residential drainage under lawns and garden beds. Class SN8 heavy-wall PVC is specified for deep excavations or areas subject to heavy vehicular traffic."
      },
      {
        question: "How does Craft Civil manage drainage for new residential builds and subdivisions?",
        answer: "We verify connection depths using Auckland Council GeoMaps, trench and bed pipework in GAP20 aggregate, core public manholes for network tie-ins, and prepare As-Built CAD plans for Council CCC sign-offs."
      }
    ]
  },
  {
    slug: "drainage",
    title: "Public & Private Drainage",
    shortDesc: "Complete civil drainage solutions across Greater Auckland — stormwater networks, sewer mains, residential drainage, and council connections.",
    desc: "Comprehensive public and private drainage contracting for residential developments, subdivisions, and commercial infrastructure across Auckland. Certified, council-compliant, and built for durability under heavy rainfall.",
    features: [
      "Public stormwater and sewer main extensions",
      "Private residential drainage and subsoil relief",
      "Auckland Council & Watercare approved connections",
      "Precast concrete manholes and catch pit construction",
      "Novacoil French drains and retaining wall drainage",
      "PS3 Producer Statement certification and As-Builts"
    ],
    images: [
      { src: "/media/public-drainage/public-drainage-01.jpeg", alt: "Public stormwater main pipeline installation in South Auckland", caption: "South Auckland • Heavy Public Stormwater Trunk Line" },
      { src: "/media/private-drainage/private-drainage-01.jpeg", alt: "Residential stormwater pipe installation in Papakura, Auckland", caption: "Papakura • Stormwater Pipe Trenching & Alignment" },
      { src: "/media/public-drainage/public-drainage-02.jpeg", alt: "Reinforced precast concrete manhole installation in Manukau", caption: "Manukau • 1050mm Precast Concrete Manhole Chamber" },
      { src: "/media/private-drainage/private-drainage-04.jpeg", alt: "Subsoil drainage trench with Novacoil pipe in North Shore", caption: "North Shore • Geotextile-Wrapped Novacoil Subsoil Drain" },
      { src: "/media/public-drainage/public-drainage-05.jpeg", alt: "Completed public drainage main extension in Central Auckland", caption: "Central Auckland • Public Drainage Extension & Tie-In" }
    ],
    contentSections: [
      {
        heading: "Full-Spectrum Civil Drainage Across Greater Auckland",
        paragraphs: [
          "Craft Civil Contractors delivers end-to-end civil drainage solutions across Greater Auckland, uniting large-scale [public drainage infrastructure](/services/public-drainage) with precision on-site [private drainage systems](/services/private-drainage). From initial site levels and Auckland Council GeoMaps GIS analysis through deep trenching, laser-guided falls, and council sign-offs, we handle every stage of your drainage network.",
          "Whether you are extending a council stormwater main for a multi-unit subdivision, installing on-site stormwater retention under Unitary Plan SMAF rules, or upgrading an aging sewer lateral, our certified team ensures full compliance with NZ Building Code Clauses E1 (Surface Water) and G13 (Foul Water)."
        ]
      },
      {
        heading: "Public Infrastructure & Council Network Tie-Ins",
        paragraphs: [
          "Connecting new developments to municipal infrastructure requires strict adherence to Watercare and Auckland Council civil standards. We manage public stormwater and sewer main extensions, live network tie-ins, precast concrete manholes (1050mm/1200mm chambers), and roadside catch pits with silt arrestors.",
          "Our crews operate certified trench safety boxes, manage dewatering in saturated clay, and install heavy-wall Class SN8 PVC-U, concrete, or PE pipes bedded in laser-graded GAP20 aggregate. Every public line is pressure tested, CCTV inspected, and documented for council asset handovers."
        ]
      },
      {
        heading: "Private Residential & Commercial Site Drainage",
        paragraphs: [
          "Within property boundaries, effective drainage prevents foundation movement, surface ponding, and damp section issues. We install comprehensive residential stormwater systems, French drains, perforated Novacoil subsoil relief behind retaining walls, and integrated [rainwater tank installations](/services/rainwater-tank-install).",
          "All private laterals are laid with laser-verified falls to guarantee self-cleansing velocity. On completion, we supply scaled As-Built plans and issue Producer Statement PS3 documentation required for Code Compliance Certificate (CCC) issuance."
        ]
      }
    ],
    keyTasks: [
      "Public stormwater and wastewater main pipeline extensions",
      "Private on-site residential and commercial drain installation",
      "Watercare and Auckland Council network connections and live tie-ins",
      "Reinforced concrete manhole and catch pit construction",
      "Novacoil subsoil drainage and French drain installation",
      "Driveway channel drains and soakhole construction",
      "Trench excavation with shoring boxes and GAP20 aggregate bedding",
      "Low-pressure air and hydrostatic water pressure testing",
      "CCTV camera inspections and pre-pour quality checks",
      "Preparation of scaled As-Built plans and PS3 certification"
    ],
    whyTrustUs: [
      "Certified drainlaying team with 15+ years of Auckland civil experience",
      "Comprehensive capabilities spanning both public infrastructure and private sites",
      "Full compliance with NZ Building Code E1, G13, and Auckland Council standards",
      "Transparent project pricing with detailed line-item quotes",
      "Complete site documentation including As-Built plans and PS3 Producer Statements",
      "Fully insured with $2M public liability coverage",
      "Fast response with standby teams available across Greater Auckland"
    ],
    faqs: [
      {
        question: "What is the difference between public and private drainage in Auckland?",
        answer: "Private drainage covers all stormwater and wastewater pipes within your property boundary up to the point of connection. Public drainage refers to council-owned or Watercare-owned mains, trunk lines, and manholes located in road reserves or public easements. We are qualified to work on both sides of the boundary."
      },
      {
        question: "Do drainage works in Auckland require council building consent?",
        answer: "New drainage installations, major alterations, and connections to public mains require Auckland Council Building Consent. On completion, a certified drainlayer must issue a Producer Statement (PS3) and provide As-Built plans for your Code Compliance Certificate (CCC)."
      },
      {
        question: "How do you ensure proper fall on flat or challenging Auckland sections?",
        answer: "Our crews use precision pipe lasers and optical site levels to establish exact minimum gradients (e.g., 1:60 or 1:100 as specified by NZBC E1/G13), ensuring self-cleansing velocity even on challenging flat or low-lying sites."
      },
      {
        question: "Can Craft Civil manage both earthworks and drainage on the same site?",
        answer: "Yes. Combining site scrape, bulk earthworks, and drainage trenching under one contractor reduces scheduling delays, eliminates site friction, and lowers overall project costs."
      }
    ]
  }
];

export const serviceOptions = [
  "Private Drainage",
  "Public Drainage",
  "Rainwater Tank Installation",
  "Wastewater & Sewer Systems",
  "Drain Unblocking",
  "Plumbing",
  "Earthworks & Demolition",
  "Concrete & Civil Work",
  "Drainlaying",
  "Emergency Service",
  "Other"
];
